import React from "react";
import {
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface Option {
  value: string | number;
  label: string | number | React.ReactNode;
}
interface SelectOptionsProps {
  options?: Option[];
  label?: string;
  disabled?: boolean;
  value?: string | number | undefined;
  isMandatory: boolean;
  onChange: (value: string | number) => void;
  helperText?: string;
  error?: boolean;
  padding?: string;
  placeholder?: string;
  isApplicant?: boolean;
  lableClass?: string;
}

const SelectOptions: React.FC<SelectOptionsProps> = ({
  options,
  label,
  disabled = false,
  value,
  isMandatory = false,
  onChange,
  helperText,
  error,
  placeholder,
  isApplicant = false,
  lableClass
}) => {
  const [open, setOpen] = React.useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);
  const hasLabel = Boolean(label?.trim());

  React.useEffect(() => {
    if (!open) return;

    let cleanupFn: (() => void) | undefined;

    const handleScroll = (event: Event) => {
      // Prevent closing if scrolling inside the dropdown menu itself
      const target = event.target;
      
      // Check if target is an HTMLElement with closest method
      if (target && typeof (target as HTMLElement).closest === 'function') {
        const htmlTarget = target as HTMLElement;
        if (htmlTarget.closest('.MuiPopover-root') || htmlTarget.closest('.MuiMenu-paper')) {
          return;
        }
      }
      
      setOpen(false);
    };

    // Small delay to ensure dropdown is rendered
    const timer = setTimeout(() => {
      // Listen to all scroll events
      document.addEventListener("scroll", handleScroll, true);
      window.addEventListener("scroll", handleScroll, true);
      
      // Get all potentially scrollable parents
      const elementsToCleanup: HTMLElement[] = [];
      let element: HTMLElement | null = selectRef.current;
      
      while (element) {
        element.addEventListener("scroll", handleScroll, true);
        elementsToCleanup.push(element);
        element = element.parentElement;
      }

      // Store cleanup function
      cleanupFn = () => {
        document.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("scroll", handleScroll, true);
        elementsToCleanup.forEach(el => {
          el.removeEventListener("scroll", handleScroll, true);
        });
      };
    }, 10);

    return () => {
      clearTimeout(timer);
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [open]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    onChange(event.target.value as string | number);
  };

  const labelId = `select-options-label-${label
    ?.replace(/\s+/g, "-")
    .toLowerCase()}`;

  const EmploymentTypeTooltipContent = (
    <Typography variant="caption" component="div">
      Select the option that best describes your occupation:
      <ul style={{ listStyleType: "disc", paddingLeft: "15px" }}>
        {isApplicant === true ? (
          <></>
        ) : (
          <li>
            <b>Farmer:</b> Engaged in agriculture, farming, or allied activities.
          </li>
        )}
        <li>
          <b>Salaried:</b> Employed with a fixed salary.
        </li>
        <li>
          <b>SEP:</b> Self-Employed Professional (e.g., professional practices such as Doctor, Lawyer, CA, Architect).
        </li>
        <li>
          <b>SENP:</b> Self-Employed Non-Professional (e.g., Trader, Shop Owner, Entrepreneur).
        </li>
      </ul>
    </Typography>
  );

  const CourseTypeTooltipContent = (
    <>
      <Typography variant="caption">
        <ul style={{ listStyleType: "disc", paddingLeft: "15px" }}>
          <li>
            <b>MBA:</b> Professional management degrees
          </li>
          <li>
            <b>Non-STEM:</b> Arts, Humanities, and other non-technical fields.
          </li>
          <li>
            <b>STEM:</b> Programs in Science, Technology, Engineering, and
            Mathematics
          </li>
        </ul>
      </Typography>
    </>
  );

  const tooltipContent =
    label?.toLowerCase() === "employment type"
      ? EmploymentTypeTooltipContent
      : label?.toLowerCase() === "course type"
      ? CourseTypeTooltipContent
      : "";

  const normalizedLabel = label?.toLowerCase();

  return (
    <div className="form-container" ref={selectRef}>
      <div>
        {hasLabel && (
          <label
            id={labelId}
            className={lableClass === "portal-text-lable" ? "portal-text-lable" : "text-lable"}
            htmlFor="custom-select"
          >
            {label} {isMandatory && <span style={{ color: "red" }}>*</span>}
          </label>
        )}

        {hasLabel &&
          (normalizedLabel === "employment type" ||
          normalizedLabel === "course type") && (
          <Tooltip 
            title={tooltipContent} 
            placement="top-start" 
            arrow
            enterTouchDelay={0}     // Displays immediately on touch
            leaveTouchDelay={3000}  // Keeps tooltip open for 3s on touch
          >
            <IconButton
              size="small"
              onClick={(e) => e.stopPropagation()} // Prevents dropdown from opening on icon tap
              sx={{
                ml: "5px",
                padding: 0,
                "&:hover": { background: "transparent" },
              }}
              aria-label="Salesperson contact details"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: "14px",
                  color: "#a5a3ae",
                  cursor: "pointer",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <Select
        labelId={labelId}
        id="custom-select"
        error={error}
        value={value || ""}
        open={open}
        onChange={(e) => {
          handleChange(e);
          setOpen(false); // close on selection
        }}
        fullWidth
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disabled={disabled}
        size="small"
        displayEmpty
        renderValue={(selected: string | number | undefined) => {
          if (selected === "" || selected === undefined) {
            return (
              <span style={{ color: "#a5a3ae" }}>
                {placeholder || `Select ${label}`}
              </span>
            );
          }
          const match = options?.find(
            (opt) => String(opt.value) === String(selected)
          );
          // Show label for selected option; fallback to selected value string
          return (match?.label ?? String(selected)) as React.ReactNode;
        }}
        MenuProps={{
          disablePortal: false,
          disableScrollLock: true,
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: 250,
              zIndex: 1500,
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
        }}
        sx={{
          height: "38px",
          borderRadius: "6px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: error ? "#d32f2f" : "#1CB1B1",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: error ? "#d32f2f" : "#1CB1B1",
          },
          "& .MuiSelect-select": {
            padding: "7px 14px 7px 14px !important",
            fontSize: "11px",
            color: "#a5a3ae",
            fontWeight: "400",
            textTransform: "capitalize",
          },
        }}
      >
        {options?.map((option, index) => (
          <MenuItem
            key={String(option?.value ?? index)}
            value={option?.value}
            className="disbursement-inside-page-lable"
            sx={{ textTransform: "capitalize !important" }}
          >
            {option?.label}
          </MenuItem>
        ))}
      </Select>

      {/* Helper Text */}
      {helperText && (
        <FormHelperText
          error={error}
          style={{ marginTop: "4px", fontSize: "11px" }}
        >
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default SelectOptions;
